import { useMemo } from 'react';

import { useWorkspace } from '@treams/treams-hooks';
import { FeatureName, WorkspaceFeatureStatus, useFeaturesQuery } from '@treams/treams-network';

import { ScoreCategory, ScoreModuleState, type ScoreModuleStatus } from '../TreamsScore.types';

const CATEGORY_FEATURE: Record<ScoreCategory, FeatureName> = {
	[ScoreCategory.Engagement]: FeatureName.Engagement,
	[ScoreCategory.Targets]: FeatureName.Goals,
	[ScoreCategory.Feedback]: FeatureName.Feedback,
	[ScoreCategory.OneOnOne]: FeatureName.OneOnOnes,
	[ScoreCategory.Conversations]: FeatureName.Evaluations,
};

export const useScoreModuleStatus = (): ScoreModuleStatus => {
	const { data } = useFeaturesQuery({
		variables: { id: useWorkspace().id },
		fetchPolicy: 'cache-first',
	});

	return useMemo<ScoreModuleStatus>(() => {
		const byFeature = new Map(data?.workspace.features.map((f) => [f.feature.id, f]) ?? []);

		const stateFor = (category: ScoreCategory): ScoreModuleState => {
			const feature = byFeature.get(CATEGORY_FEATURE[category]);
			const isEnabled = feature?.status === WorkspaceFeatureStatus.Enabled;

			if (isEnabled) return ScoreModuleState.Enabled;
			return feature?.inPlan ? ScoreModuleState.Disabled : ScoreModuleState.Unavailable;
		};

		return Object.fromEntries(
			(Object.keys(CATEGORY_FEATURE) as ScoreCategory[]).map((category) => [
				category,
				stateFor(category),
			]),
		) as ScoreModuleStatus;
	}, [data]);
};
